import type { MerchantProfileView } from '../../merchant/api.js';

export type ResearchIdea = NonNullable<
  MerchantProfileView['result']
>['recommendations'][number];
export type ResearchReference = NonNullable<ResearchIdea['references']>[number];
export type ReferenceIdea = Pick<
  ResearchIdea,
  'artifactId' | 'title' | 'references'
> & { readonly basis?: ResearchIdea['basis'] | 'TOPIC_BASED' };
export type ResearchOutcome = 'live' | 'mixed' | 'saved' | 'business' | 'unknown';

/** Only released, linked evidence supports an outcome. Empty arrays are not success. */
export function researchOutcome(ideas: readonly ResearchIdea[]): ResearchOutcome {
  if (ideas.length === 0) return 'unknown';
  if (ideas.every((idea) => idea.basis === 'BUSINESS_BASED')) return 'business';
  const linked = ideas.filter((idea) => idea.basis !== 'BUSINESS_BASED');
  if (linked.some((idea) => !idea.references?.length)) return 'unknown';
  const origins = linked.flatMap((idea) =>
    (idea.references ?? []).map((ref) => ref.origin),
  );
  if (origins.some((origin) => origin === null)) return 'unknown';
  const live = origins.includes('LIVE');
  const saved = origins.includes('SAVED');
  return live && saved ? 'mixed' : live ? 'live' : saved ? 'saved' : 'unknown';
}

/** Keep differing measurement snapshots distinct; never replace a saved date with a live date. */
export function referenceGroups(ideas: readonly ReferenceIdea[]) {
  const groups = new Map<
    string,
    { key: string; reference: ResearchReference; usedBy: ReferenceIdea[] }
  >();
  for (const idea of ideas) {
    for (const reference of idea.references ?? []) {
      const key = JSON.stringify(reference);
      const existing = groups.get(key);
      if (existing) {
        if (!existing.usedBy.some((item) => item.artifactId === idea.artifactId))
          existing.usedBy.push(idea);
      } else groups.set(key, { key, reference, usedBy: [idea] });
    }
  }
  return [...groups.values()];
}

/** No arbitrary markup, credential-bearing URL or third-party host becomes an iframe. */
export function instagramReferenceUrl(reference: ResearchReference): string | null {
  if (reference.platform !== 'INSTAGRAM') return null;
  const match =
    /^https:\/\/www\.instagram\.com\/(reel|reels|p)\/([A-Za-z0-9_-]+)\/$/u.exec(
      reference.canonicalUrl,
    );
  return match === null ? null : reference.canonicalUrl;
}

export const researchUi = {
  en: {
    title: 'Research',
    complete: 'Research complete',
    references: 'Supporting references',
    live: 'New references',
    mixed: 'New and saved references',
    saved: 'Saved references',
    business: 'Ideas based on your business',
    unknown: 'Reference provenance unavailable',
    businessNote:
      'These ideas use your confirmed business context, without external performance evidence.',
    referencesNote:
      'These are the references linked to the released ideas, not every post searched. Metrics describe the original content, not a promise of results.',
    ideas: 'View ideas',
    details: 'Reference details',
    caption: 'Original caption',
    unavailable: 'Not available',
    unknownMetrics:
      'The metric definition is unavailable; counts cannot be compared reliably.',
    source: 'Source language',
    content: 'Content language',
    usedBy: 'Used by',
    preview: 'Load Instagram preview',
    close: 'Close preview',
    previewNote:
      'Loads content from Instagram. Playback may require cookies or sign-in; the original video is not translated.',
    previewFailed: 'Preview could not be loaded. Open the original instead.',
    original: 'Open original',
    rationale: 'Treatment and references',
    prepare: 'Prepare content for review',
    preparing: 'Preparing…',
    selectionNote:
      'Select an idea, then prepare it for review. Production requires a separate confirmation.',
    readOnly:
      'Previously released ideas. Viewing them does not change the current selection or restart work.',
    languages: {
      en: 'English',
      ar: 'Arabic',
      EN: 'English',
      AR: 'Arabic',
      UNKNOWN: 'Not available',
    },
  },
  ar: {
    title: 'البحث',
    complete: 'اكتمل البحث',
    references: 'المراجع الداعمة',
    live: 'مراجع جديدة',
    mixed: 'مراجع جديدة ومحفوظة',
    saved: 'مراجع محفوظة',
    business: 'أفكار مبنية على نشاطك',
    unknown: 'مصدر المراجع غير متاح',
    businessNote: 'تعتمد هذه الأفكار على سياق نشاطك المعتمد، دون أدلة أداء خارجية.',
    referencesNote:
      'هذه المراجع مرتبطة بالأفكار المقدّمة، وليست كل المنشورات التي شملها البحث. المقاييس تخص المحتوى الأصلي ولا تضمن النتائج.',
    ideas: 'عرض الأفكار',
    details: 'تفاصيل المرجع',
    caption: 'الوصف الأصلي',
    unavailable: 'غير متاح',
    unknownMetrics: 'تعريف المقياس غير متاح؛ لا يمكن مقارنة الأرقام بشكل موثوق.',
    source: 'لغة المصدر',
    content: 'لغة المحتوى',
    usedBy: 'مستخدم في',
    preview: 'تحميل معاينة إنستغرام',
    close: 'إغلاق المعاينة',
    previewNote:
      'يحمّل محتوى من إنستغرام. قد يتطلب التشغيل ملفات تعريف الارتباط أو تسجيل الدخول؛ الفيديو الأصلي غير مترجم.',
    previewFailed: 'تعذر تحميل المعاينة. افتح المحتوى الأصلي بدلاً منها.',
    original: 'فتح الأصل',
    rationale: 'المعالجة والمراجع',
    prepare: 'تحضير المحتوى للمراجعة',
    preparing: 'جارٍ التحضير…',
    selectionNote: 'اختر فكرة ثم حضّرها للمراجعة. يتطلب الإنتاج تأكيداً منفصلاً.',
    readOnly:
      'الأفكار المقدّمة سابقاً. عرضها لا يغيّر الاختيار الحالي ولا يعيد تشغيل العمل.',
    languages: {
      en: 'الإنجليزية',
      ar: 'العربية',
      EN: 'الإنجليزية',
      AR: 'العربية',
      UNKNOWN: 'غير متاح',
    },
  },
} as const;

export function contentLanguage(
  value: string | null | undefined,
  language: 'en' | 'ar',
): string {
  const labels = researchUi[language].languages;
  return value !== null && value !== undefined && value in labels
    ? labels[value as keyof typeof labels]
    : researchUi[language].unavailable;
}
